import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { RequirementType } from '@/utils/membership/types'
import type { RequirementFormValues } from '../schema'
import type { CategoryRequirement } from '../types'

interface RequirementDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  requirement?: CategoryRequirement
  onSubmit: (values: RequirementFormValues) => Promise<void> | void
  isSubmitting?: boolean
}

const REQUIREMENT_TYPE_OPTIONS = Object.values(RequirementType).map((type) => ({
  label: type.charAt(0) + type.slice(1).toLowerCase(),
  value: type,
}))

const emptyForm: RequirementFormValues = {
  label: '',
  description: '',
  mandatory: true,
  type: RequirementType.TEXT,
}

export function RequirementDialog({
  open,
  onOpenChange,
  requirement,
  onSubmit,
  isSubmitting,
}: RequirementDialogProps) {
  const [formState, setFormState] = useState<RequirementFormValues>(emptyForm)
  const [errors, setErrors] = useState<{ label?: string }>({})

  useEffect(() => {
    if (open) {
      setFormState({
        id: requirement?.id,
        label: requirement?.label ?? '',
        description: requirement?.description ?? '',
        mandatory: requirement?.mandatory ?? true,
        type: requirement?.type ?? RequirementType.TEXT,
        validationJson: requirement?.validationJson,
        createdAt: requirement?.createdAt,
        updatedAt: requirement?.updatedAt,
      })
      setErrors({})
    } else {
      setFormState(emptyForm)
    }
  }, [open, requirement])

  const validate = () => {
    const newErrors: { label?: string } = {}

    if (!formState.label?.trim()) {
      newErrors.label = 'Requirement label is required'
    } else if (formState.label.length > 255) {
      newErrors.label = 'Label must be 255 characters or less'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!validate()) return

    await onSubmit({
      ...formState,
      label: formState.label.trim(),
      description: formState.description?.trim(),
    })
  }

  const handleCancel = () => {
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{requirement ? 'Edit Requirement' : 'Create Requirement'}</DialogTitle>
            <DialogDescription>
              Configure the label, type, and behaviour of this membership requirement.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="req-label">
                Requirement Label <span className="text-destructive">*</span>
              </Label>
              <Input
                id="req-label"
                placeholder="e.g., Years of experience"
                value={formState.label}
                onChange={(event) =>
                  setFormState((prev) => ({ ...prev, label: event.target.value }))
                }
                className={errors.label ? 'border-destructive' : ''}
                autoFocus
              />
              {errors.label && (
                <p className="text-sm text-destructive">{errors.label}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Requirement Type</Label>
              <Select
                value={formState.type}
                onValueChange={(value) =>
                  setFormState((prev) => ({ ...prev, type: value as RequirementType }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select requirement type" />
                </SelectTrigger>
                <SelectContent>
                  {REQUIREMENT_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                Determines the input applicants will provide when completing this requirement.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="req-description">Helper Text (optional)</Label>
              <Textarea
                id="req-description"
                placeholder="Add extra instructions or context for applicants."
                value={formState.description ?? ''}
                onChange={(event) =>
                  setFormState((prev) => ({ ...prev, description: event.target.value }))
                }
                rows={4}
                className="resize-none"
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label htmlFor="req-mandatory" className="text-base">
                  Mandatory requirement
                </Label>
                <p className="text-sm text-muted-foreground">
                  Applicants must satisfy this requirement to proceed.
                </p>
              </div>
              <Switch
                id="req-mandatory"
                checked={formState.mandatory}
                onCheckedChange={(checked) =>
                  setFormState((prev) => ({ ...prev, mandatory: checked }))
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? 'Saving...'
                : requirement
                  ? 'Update Requirement'
                  : 'Create Requirement'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
